import { MessageBus } from "./message_bus.js";
import { Utils } from "./utils.js";

// The setup checklist beside the video: one row per item, so the tester can
// tell whether their setup works before they start.
//
// Every row is computed from the facts RecordingManager reports, so the order
// in which events arrive can never leave a row wrong.
//
// Rows follow the test contract:
//   [data-testid="setup-check-<item>"][data-state="unknown|ok|warn|fail|off"]
//
// The "How to fix" texts stay here and reach the page only in the info modal.
export class SetupCheck {
  static ITEMS = ["browser", "screen", "game-audio", "microphone", "quality", "picture"];

  static ICONS = {
    unknown: "bi-circle text-body-secondary",
    ok: "bi-check-circle-fill text-success",
    warn: "bi-exclamation-triangle-fill text-warning",
    fail: "bi-x-circle-fill text-danger",
    off: "bi-dash-circle text-body-secondary",
  };

  static #MIC_BLOCKED_FIX = "Click the icon at the left of the address bar, open *Site settings*, allow *Microphone*, then switch the mic on again.";

  facts = {
    browserMissing: [],
    screen: null, // { surface, label } while a screen is shared
    screenProblem: null,
    gameAudio: false,
    gameHeard: null, // the tester's answer after the replay
    micOn: false,
    micLive: false,
    micProblem: null,
    micStopped: false,
    micBlocked: false, // blocked before the tester asked for it
    micHeard: false, // the meter went over the threshold
    voiceHeard: null, // the tester's answer after the replay
    picture: null, // null | "ok" | "warn" | "black"
    quality: null, // the frame rates of the last test, see RecordingManager.frameRateMeasured()
  };

  #rows = {};
  #rendered = {};
  #onChange;

  constructor(element, onChange = null) {
    this.#onChange = onChange;

    SetupCheck.ITEMS.forEach((item) => {
      const rowElement = element.querySelector(`[data-testid="setup-check-${item}"]`);

      this.#rows[item] = {
        element: rowElement,
        icon: rowElement.querySelector(".setup-check-icon"),
        text: rowElement.querySelector(".setup-check-text"),
        fixButton: rowElement.querySelector(".setup-check-fix"),
        retestButton: rowElement.querySelector(".setup-check-retest"),
      };

      this.#rows[item].fixButton.addEventListener("click", () => {
        const fix = this.#rendered[item]?.fix;
        if (fix) {
          MessageBus.info(fix);
        }
      });
    });

    this.#render(false);
  }

  update(facts) {
    Object.assign(this.facts, facts);
    this.#render(true);
  }

  state(item) {
    return this.#rendered[item]?.state ?? "unknown";
  }

  states() {
    return Object.fromEntries(SetupCheck.ITEMS.map((item) => [item, this.state(item)]));
  }

  hasFailures() {
    return SetupCheck.ITEMS.some((item) => this.state(item) === "fail");
  }

  // Only touches the rows that changed, and tells the owner if any did
  #render(notify) {
    let changed = false;

    SetupCheck.ITEMS.forEach((item) => {
      const row = SetupCheck.describeRow(item, this.facts);
      const previous = this.#rendered[item];

      if (previous && previous.state === row.state && previous.text === row.text && previous.fix === row.fix && previous.retest === row.retest) {
        return;
      }

      this.#rendered[item] = row;
      this.#paint(item, row);
      changed = true;
    });

    if (changed && notify && this.#onChange) {
      this.#onChange();
    }
  }

  #paint(item, { state, text, fix, retest }) {
    const row = this.#rows[item];
    row.element.dataset.state = state;
    row.icon.className = `bi setup-check-icon ${SetupCheck.ICONS[state]}`;
    row.text.textContent = text;
    row.fixButton.classList.toggle("d-none", !fix);
    row.retestButton?.classList.toggle("d-none", !retest);
  }

  // Rows :: INI
  // Returns { state, text, fix, retest } for one item. fix is markdown, or
  // null. retest offers a new test recording in the row.
  static describeRow(item, facts) {
    switch (item) {
      case "browser":
        return SetupCheck.#browserRow(facts);
      case "screen":
        return SetupCheck.#screenRow(facts);
      case "game-audio":
        return SetupCheck.#gameAudioRow(facts);
      case "microphone":
        return SetupCheck.#microphoneRow(facts);
      case "quality":
        return SetupCheck.#qualityRow(facts);
      case "picture":
        return SetupCheck.#pictureRow(facts);
    }
  }

  static #browserRow({ browserMissing }) {
    if (browserMissing.length === 0) {
      return { state: "ok", text: "Can record the Play Session", fix: null };
    }

    return {
      state: "fail",
      text: "Cannot record here",
      fix: `This browser cannot record the Play Session (missing: ${browserMissing.join(", ")}).\n\nOpen this link in Chrome, Edge or Firefox on a computer.`,
    };
  }

  static #screenRow({ screen, screenProblem }) {
    if (screen === null) {
      if (screenProblem) {
        return { state: "fail", text: screenProblem.rowText, fix: screenProblem.fix || null };
      }

      return { state: "unknown", text: "Not shared yet", fix: null };
    }

    switch (screen.surface) {
      case "monitor":
        return { state: "ok", text: "Entire screen", fix: null };
      case "window": {
        const name = SetupCheck.#windowName(screen.label);
        return {
          state: "warn",
          text: name ? `One window: ${name}` : "One window",
          fix: "Only this window is recorded. If the game opens another window or switches to fullscreen, the recording can turn black.\n\nClick **Change screen** and choose *Entire screen*.",
        };
      }
      case "browser":
        return {
          state: "fail",
          text: "A browser tab: the game will not be recorded",
          fix: "You shared a browser tab, so your game will not be recorded.\n\nClick **Change screen** and choose *Entire screen*.",
        };
      default:
        return { state: "ok", text: "Screen shared", fix: null };
    }
  }

  // Chrome labels window captures "window:<id>:0"; only a real title is worth showing
  static #windowName(label) {
    if (!label || /^window:\d+/.test(label)) {
      return null;
    }

    return label;
  }

  static #gameAudioRow({ screen, gameAudio, gameHeard }) {
    if (screen === null) {
      return { state: "unknown", text: "Checked when you share your screen", fix: null };
    }

    if (!gameAudio) {
      return { state: "warn", text: "No game sound", fix: SetupCheck.#noGameAudioFix() };
    }

    if (gameHeard === false) {
      return {
        state: "warn",
        text: "Not heard in the replay",
        fix: "Check that the game is not muted and plays through this computer's speakers or headphones. Then click **Change screen** and make sure *Share system audio* is ticked.",
      };
    }

    if (gameHeard === true) {
      return { state: "ok", text: "Heard in the replay ✓", fix: null };
    }

    return { state: "ok", text: "Shared", fix: null };
  }

  static #noGameAudioFix() {
    if (Utils.isMac()) {
      return "The game sound is not being shared.\n\nIf the browser dialog shows *Share system audio*, click **Change screen** and tick it. On macOS this option is often missing when sharing the entire screen; you can still play, and mention sound problems in your comments.";
    }

    return "The game sound is not being shared.\n\nClick **Change screen** and tick *Share system audio* in the browser dialog. Firefox cannot share the system sound; use Chrome or Edge for that.";
  }

  static #microphoneRow({ micOn, micLive, micProblem, micStopped, micBlocked, micHeard, voiceHeard }) {
    if (micProblem) {
      return { state: "fail", text: micProblem.rowText, fix: micProblem.fix || null };
    }

    if (micStopped) {
      return {
        state: "warn",
        text: "Stopped, switch it on again",
        fix: "Your microphone stopped, maybe it was unplugged. The recording continues without it.\n\nPlug it back in and switch the mic on again.",
      };
    }

    if (!micOn) {
      if (micBlocked) {
        return { state: "fail", text: "Blocked by the browser", fix: SetupCheck.#MIC_BLOCKED_FIX };
      }

      return { state: "off", text: "Off: switch it on to record your voice", fix: null };
    }

    if (!micLive) {
      return { state: "unknown", text: "Asking for the microphone…", fix: null };
    }

    if (voiceHeard === false) {
      return {
        state: "warn",
        text: "Not heard in the replay",
        fix: "Check that the right microphone is selected (click the icon at the left of the address bar, or open your system sound settings) and that it is not muted. Then switch the mic off and on again and run a new test.",
      };
    }

    if (voiceHeard === true) {
      return { state: "ok", text: "Heard in the replay ✓", fix: null };
    }

    if (micHeard) {
      return { state: "ok", text: "We can hear you ✓", fix: null };
    }

    return { state: "ok", text: "On, say something", fix: null };
  }

  // The capture rate says what the screen delivered, the encode rate what the
  // recording kept (D4, D7)
  static #qualityRow({ quality }) {
    if (quality === null) {
      return { state: "unknown", text: "Measured by the test recording", fix: null };
    }

    const { captureFps, encodeFps, averageFps } = quality;

    if (captureFps !== null && captureFps < FrameRateMeter.CAPTURE_HEALTHY_FPS) {
      return {
        state: "warn",
        text: `Only ${Math.round(captureFps)} fps from the screen: move during the test`,
        fix: "Your screen sent few frames during the test. That happens when nothing moves on the screen, or when the game itself runs slowly.\n\nHave the game running, play or move around, and click **Test again**.",
        retest: true,
      };
    }

    if (encodeFps === null) {
      return { state: "unknown", text: "Watch the whole replay to measure it", fix: null };
    }

    // A screen still for part of the test averages few frames. The recording
    // kept them, so it has the rate the screen reached when it moved (D8).
    const keptUp = captureFps !== null && FrameRateMeter.encoderKeptUp(encodeFps, averageFps);

    if (encodeFps >= FrameRateMeter.ENCODE_HEALTHY_FPS || keptUp) {
      const fps = encodeFps >= FrameRateMeter.ENCODE_HEALTHY_FPS ? encodeFps : captureFps;
      const size = quality.width && quality.height ? `${quality.width}×${quality.height}` : SetupCheck.#profileLabel(quality.profile);
      return { state: "ok", text: `~${Math.round(fps)} fps at ${size} ✓`, fix: null };
    }

    if (quality.steppedDownTo) {
      const from = SetupCheck.#profileLabel(quality.profile);
      const to = SetupCheck.#profileLabel(quality.steppedDownTo);
      return {
        state: "warn",
        text: `${Math.round(encodeFps)} fps at ${from}: now ${to}, test again`,
        fix: `Your computer struggles to record at ${from} while the game runs, so the Play Session will be recorded at ${to} for smoother video.\n\nClick **Test again** to check it.`,
        retest: true,
      };
    }

    if (captureFps === null) {
      return {
        state: "warn",
        text: `Only ${Math.round(encodeFps)} fps recorded`,
        fix: "The test recording kept few frames per second. Play or move around during the test, and close other programs that use a lot of your computer (other recorders, video calls), then click **Test recording** again.\n\nYou can still start the Play Session.",
      };
    }

    return {
      state: "warn",
      text: `Only ${Math.round(encodeFps)} fps recorded`,
      fix: "Your computer struggles to record while the game runs, even at the lowest quality. Close other programs that use a lot of your computer (other recorders, video calls, browser tabs with videos) and click **Test recording** again.\n\nYou can still start the Play Session.",
    };
  }

  // "720p30" → "720p"
  static #profileLabel(profile) {
    return profile ? `${profile.height}p` : "";
  }

  static #pictureRow({ picture }) {
    switch (picture) {
      case "ok":
        return { state: "ok", text: "Picture captured", fix: null };
      case "warn":
        return {
          state: "warn",
          text: "Went black during the test",
          fix: SetupCheck.#blackPictureFix("The picture went black at the end of the test. That is fine if the game was loading. Otherwise:"),
        };
      case "black":
        return {
          state: "fail",
          text: "The capture is black",
          fix: SetupCheck.#blackPictureFix("The recording shows a black picture."),
        };
      default:
        return { state: "unknown", text: "Checked by the test recording", fix: null };
    }
  }

  static #blackPictureFix(intro) {
    const steps = [];

    if (Utils.isMac()) {
      steps.push("On macOS open *System Settings → Privacy & Security → Screen Recording*, switch your browser on, then quit and reopen the browser.");
    }

    steps.push("If your game runs in exclusive fullscreen, switch it to *Borderless* or *Windowed* in its video settings.");
    steps.push("Then click **Test recording** again.");

    return `${intro}\n\n${steps.join("\n\n")}`;
  }
  // Rows :: END

  // Problems :: INI
  // The one table for screen and mic failures, keyed by the error name (C2).
  // device: "screen", "microphone" or "recorder".
  //
  // Returns null when there is nothing to say (the tester closed the screen
  // picker), or { rowText, summary, fix }. rowText is null when the error is
  // not about the device itself.
  static describeProblem(error, device) {
    const name = error?.name;
    const isMic = device === "microphone";
    const deviceName = isMic ? "microphone" : "screen";

    if (name === "NotAllowedError") {
      if (/by system/i.test(String(error.message))) {
        const setting = isMic ? "Microphone" : "Screen Recording";
        return {
          rowText: "Blocked by your computer",
          summary: `Your computer does not allow this browser to use the ${deviceName}.`,
          fix: `On macOS open *System Settings → Privacy & Security → ${setting}*, switch your browser on, then quit and reopen the browser.`,
        };
      }

      if (isMic) {
        return {
          rowText: "Blocked by the browser",
          summary: "Your browser blocked the microphone.",
          fix: SetupCheck.#MIC_BLOCKED_FIX,
        };
      }

      if (device === "screen") {
        // A refusal before a person could answer means the picker never showed
        if (error.withoutPicker) {
          return {
            rowText: "Blocked by the browser",
            summary: "Your browser blocked screen sharing.",
            fix: "Click the icon at the left of the address bar, open *Site settings*, allow screen sharing, then click **Test recording** again. If this computer is managed by your work or school, screen sharing may be turned off for you; try another computer.",
          };
        }

        return null;
      }
    }

    switch (name) {
      case "NotFoundError":
        if (isMic) {
          return { rowText: "No microphone found", summary: "No microphone was found.", fix: "Plug one in and switch the mic on again." };
        }
        return { rowText: "No screen found", summary: "No screen was found to share.", fix: "" };
      case "NotReadableError":
      case "AbortError":
        return {
          rowText: "In use by another app",
          summary: `Your ${deviceName} is being used by another app (Discord, OBS, Zoom…).`,
          fix: "Close it and try again.",
        };
      case "NotSupportedError":
        return {
          rowText: null,
          summary: "This browser cannot record the Play Session.",
          fix: "Please use Chrome, Edge or Firefox on a desktop computer.",
        };
      case "AudioContextNotRunning":
        return {
          rowText: null,
          summary: "Your browser paused the sound of this page, so the recording would be empty.",
          fix: "Click anywhere on the page and try again.",
        };
      case "ScreenShareEnded":
        // The screen row already says it is not shared
        return null;
    }

    if (isMic) {
      return { rowText: "Could not start", summary: "There was a problem activating the microphone.", fix: "" };
    }

    return { rowText: null, summary: "There was a problem recording the screen. Please try again.", fix: "" };
  }

  // The text of the alert modal for a problem
  static message(problem) {
    return problem.fix ? `${problem.summary}\n\n${problem.fix}` : problem.summary;
  }
  // Problems :: END
}

// Tells a black capture from a real picture (C6): macOS without the Screen
// Recording permission, or a game in exclusive fullscreen on Windows.
// Samples the live preview once a second into a tiny canvas.
export class BlackFrameDetector {
  static INTERVAL_MS = 1_000;
  static BLACK_STREAK = 3;
  static WIDTH = 32;
  static HEIGHT = 18;
  static MAX_MEAN_LUMA = 8;
  static MAX_PEAK_LUMA = 40; // a mouse pointer on black stays below this at 32×18

  #video;
  #canvas;
  #context;
  #intervalID = null;
  #samples = 0;
  #streak = 0;
  #sawPicture = false;

  constructor(video) {
    this.#video = video;
    this.#canvas = document.createElement("canvas");
    this.#canvas.width = BlackFrameDetector.WIDTH;
    this.#canvas.height = BlackFrameDetector.HEIGHT;
    this.#context = this.#canvas.getContext("2d", { willReadFrequently: true });
  }

  // onVerdict gets "ok", "warn", "black" or null after every sample. Black is
  // only reported after a few seconds, so a short loading screen is fine.
  start(onVerdict) {
    clearInterval(this.#intervalID);
    this.#samples = 0;
    this.#streak = 0;
    this.#sawPicture = false;

    this.#intervalID = setInterval(() => {
      this.#sample();
      onVerdict(this.#liveVerdict());
    }, BlackFrameDetector.INTERVAL_MS);
  }

  // Takes a last sample and returns the verdict for the whole test: "black"
  // when the picture never showed, "warn" when it ended black, "ok", or null
  // when nothing could be sampled.
  stop() {
    if (this.#intervalID !== null) {
      clearInterval(this.#intervalID);
      this.#intervalID = null;
      this.#sample();
    }

    if (this.#samples === 0) return null;
    if (!this.#sawPicture) return "black";
    if (this.#streak >= BlackFrameDetector.BLACK_STREAK) return "warn";
    return "ok";
  }

  #liveVerdict() {
    if (this.#streak >= BlackFrameDetector.BLACK_STREAK) {
      return this.#sawPicture ? "warn" : "black";
    }

    return this.#sawPicture ? "ok" : null;
  }

  #sample() {
    let isBlack;

    try {
      isBlack = this.#isBlack();
    } catch (error) {
      // An unreadable frame says nothing about the picture
      console.warn("BlackFrameDetector.sample()", error);
      return;
    }

    this.#samples++;

    if (isBlack) {
      this.#streak++;
    } else {
      this.#streak = 0;
      this.#sawPicture = true;
    }
  }

  // No frame at all looks black to the tester too
  #isBlack() {
    const video = this.#video;

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth === 0) {
      return true;
    }

    const { width, height } = this.#canvas;
    this.#context.clearRect(0, 0, width, height);
    this.#context.drawImage(video, 0, 0, width, height);
    const pixels = this.#context.getImageData(0, 0, width, height).data;

    let sum = 0;
    let peak = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      const luma = 0.2126 * pixels[i] + 0.7152 * pixels[i + 1] + 0.0722 * pixels[i + 2];
      sum += luma;
      peak = Math.max(peak, luma);
    }

    const mean = sum / (pixels.length / 4);
    return mean < BlackFrameDetector.MAX_MEAN_LUMA && peak < BlackFrameDetector.MAX_PEAK_LUMA;
  }
}

// Measures how many frames per second the shared screen delivers during the
// test (D4, D7). A capturer skips unchanged frames, so this is the capture
// rate; what the recording kept is measured on the replay.
//
// Counts the frames the live preview shows, and where the browser has them,
// the frames the screen track delivered: the preview stops counting while this
// tab is hidden behind the game. The busiest 2 s count, so a slow start or a
// moment spent switching windows does not lower the result.
export class FrameRateMeter {
  static WINDOW_MS = 2_000;
  static STATS_INTERVAL_MS = 500;
  static CAPTURE_HEALTHY_FPS = 24;
  static ENCODE_HEALTHY_FPS = 20;
  // Share of the delivered frames the recording must keep to count as keeping up
  static KEPT_RATIO = 0.8;

  #video;
  #track = null;
  #running = false;
  #startedAt = 0;
  #frameTimes = [];
  #statsSamples = [];
  #wasHidden = false;
  #callbackID = null;
  #intervalID = null;

  constructor(video) {
    this.#video = video;
  }

  // Lower the resolution only when the screen delivered enough frames and the
  // recording lost them; otherwise the screen is the limit, not the encoder.
  // The busiest rate says whether the screen moved; only the average over the
  // same test compares with what the recording kept (D8).
  static encoderIsBottleneck(captureFps, encodeFps, averageFps) {
    return captureFps !== null && encodeFps !== null && averageFps !== null &&
      captureFps >= FrameRateMeter.CAPTURE_HEALTHY_FPS &&
      encodeFps < FrameRateMeter.ENCODE_HEALTHY_FPS &&
      !FrameRateMeter.encoderKeptUp(encodeFps, averageFps);
  }

  // The recording kept most of the frames the screen delivered
  static encoderKeptUp(encodeFps, averageFps) {
    return encodeFps !== null && averageFps !== null &&
      encodeFps >= averageFps * FrameRateMeter.KEPT_RATIO;
  }

  start(track) {
    this.#cancel();

    this.#track = track;
    this.#running = true;
    this.#startedAt = performance.now();
    this.#frameTimes = [];
    this.#statsSamples = [];
    this.#wasHidden = document.hidden;

    if (typeof this.#video.requestVideoFrameCallback === "function") {
      const onFrame = (now) => {
        if (!this.#running) return;
        this.#frameTimes.push(now);
        this.#callbackID = this.#video.requestVideoFrameCallback(onFrame);
      };

      this.#callbackID = this.#video.requestVideoFrameCallback(onFrame);
    }

    this.#sample();
    this.#intervalID = setInterval(() => { this.#sample(); }, FrameRateMeter.STATS_INTERVAL_MS);
  }

  // Returns { captureFps, averageFps, deliveredFrames, discardedFrames }:
  // the busiest rate and the rate over the whole test, each null when nothing
  // could be measured.
  stop() {
    if (!this.#running) {
      return { captureFps: null, averageFps: null, deliveredFrames: null, discardedFrames: null };
    }

    this.#sample();
    const endedAt = performance.now();
    this.#cancel();

    const elapsedMs = endedAt - this.#startedAt;
    const windowMs = Math.min(FrameRateMeter.WINDOW_MS, elapsedMs);
    const rates = [this.#statsRate(windowMs)];
    let averageFps = this.#statsAverage();

    // The preview is not painted while the tab is hidden
    if (typeof this.#video.requestVideoFrameCallback === "function" && !this.#wasHidden) {
      rates.push(FrameRateMeter.#busiestRate(this.#frameTimes, windowMs));
      averageFps ??= FrameRateMeter.#averageRate(this.#frameTimes, elapsedMs);
    }

    const measured = rates.filter((rate) => rate !== null);
    const first = this.#statsSamples[0];
    const last = this.#statsSamples.at(-1);

    return {
      captureFps: measured.length > 0 ? Math.max(...measured) : null,
      averageFps,
      deliveredFrames: first ? last.delivered - first.delivered : null,
      discardedFrames: first ? last.discarded - first.discarded : null,
    };
  }

  #cancel() {
    this.#running = false;
    clearInterval(this.#intervalID);
    this.#intervalID = null;

    if (this.#callbackID !== null) {
      this.#video.cancelVideoFrameCallback?.(this.#callbackID);
      this.#callbackID = null;
    }
  }

  // track.stats exists in recent Chrome only, and is null for some tracks
  #sample() {
    if (document.hidden) {
      this.#wasHidden = true;
    }

    try {
      const stats = this.#track?.stats;

      if (stats && typeof stats.deliveredFrames === "number") {
        this.#statsSamples.push({
          time: performance.now(),
          delivered: stats.deliveredFrames,
          discarded: stats.discardedFrames ?? 0,
        });
      }
    } catch (error) {
      console.warn("FrameRateMeter.sample()", error);
    }
  }

  // The best rate between two samples at least a window apart, or over the
  // whole test when it was shorter
  #statsRate(windowMs) {
    const samples = this.#statsSamples;

    if (samples.length < 2 || samples.at(-1).time - samples[0].time < 1_000) {
      return null;
    }

    let best = null;
    let j = 0;

    for (let i = 0; i < samples.length; i++) {
      while (j < samples.length && samples[j].time - samples[i].time < windowMs) j++;
      if (j === samples.length) break;

      const rate = (samples[j].delivered - samples[i].delivered) * 1000 / (samples[j].time - samples[i].time);
      best = Math.max(best ?? 0, rate);
    }

    return best ?? this.#statsAverage();
  }

  // Frames delivered per second over the whole test
  #statsAverage() {
    const samples = this.#statsSamples;

    if (samples.length < 2 || samples.at(-1).time - samples[0].time < 1_000) {
      return null;
    }

    const first = samples[0];
    const last = samples.at(-1);
    return (last.delivered - first.delivered) * 1000 / (last.time - first.time);
  }

  // Most frames shown in any window of windowMs, as frames per second
  static #busiestRate(times, windowMs) {
    if (windowMs < 500) {
      return null;
    }

    let most = 0;
    let j = 0;

    for (let i = 0; i < times.length; i++) {
      while (j < times.length && times[j] - times[i] < windowMs) j++;
      most = Math.max(most, j - i);
    }

    return most * 1000 / windowMs;
  }

  // Frames shown per second over elapsedMs
  static #averageRate(times, elapsedMs) {
    return elapsedMs < 500 ? null : times.length * 1000 / elapsedMs;
  }
}
